/**
 * @file periodHelper.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-14
 * @modified 2026-08-14
 * @description Helper functions for determining current pricing period
 */

/**
 * Calculate the current period name based on today's date
 * Winter period: October 1st - April 30th (Winter YYYY-YY)
 * Summer period: May 1st - September 30th (Summer YY)
 *
 * @returns {string} Current period name (e.g., "Winter 2025-26" or "Summer 26")
 */
export const getCurrentPeriodName = () => {
  const today = new Date();
  const month = today.getMonth() + 1; // 1-12
  const year = today.getFullYear();
  const yearShort = year.toString().slice(-2);

  if (month >= 5 && month <= 9) {
    // May 1st - September 30th: Summer period
    return `Summer ${yearShort}`;
  } else if (month >= 10) {
    // October 1st - December 31st: Winter period (current year - next year)
    const nextYearShort = (year + 1).toString().slice(-2);
    return `Winter ${year}-${nextYearShort}`;
  } else {
    // January 1st - April 30th: Winter period (previous year - current year)
    const prevYear = year - 1;
    return `Winter ${prevYear}-${yearShort}`;
  }
};

/**
 * Today as a YYYY-MM-DD string in LOCAL time (period start/end dates are plain
 * dates, so comparing them as strings avoids timezone drift around midnight).
 * @returns {string}
 */
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Normalise a period date (string or Date) to a YYYY-MM-DD string.
 * @param {string|Date} value
 * @returns {string|null}
 */
export const toISODate = (value) => {
  if (!value) return null;
  return (typeof value === 'string' ? value : new Date(value).toISOString()).slice(0, 10);
};

/**
 * Has a pricing period finished? True once today is past its end_date.
 * A period with no end date is never treated as past.
 *
 * @param {Object} period - period object with an end_date
 * @returns {boolean}
 */
export const isPastPeriod = (period) => {
  const end = toISODate(period?.end_date);
  if (!end) return false;
  return todayISO() > end;
};

/**
 * Has the named pricing period finished? Looks the name up in `periods`.
 * Unknown names are never treated as past.
 *
 * @param {Array} periods - period objects with name + end_date
 * @param {string} name - period name (e.g. "Winter 2025-26")
 * @returns {boolean}
 */
export const isPastPeriodName = (periods, name) => {
  if (!Array.isArray(periods) || !name) return false;
  return isPastPeriod(periods.find(p => p.name === name));
};

/**
 * A sort key for a contract's period(s), so the Period column sorts by time
 * rather than by spelling. Sorting the names alphabetically puts "Summer 2026"
 * before "Winter 2025-26", and the names aren't even consistently formatted
 * ("Summer 26" vs "Summer 2026"), so a regex over the name is fragile — look
 * the name up in `periods` and use its start_date instead.
 *
 * A contract can be linked to several periods; the earliest one is what the
 * reader sees first in the comma-joined cell, so that's what it sorts on.
 *
 * @param {Array} periods - period objects with name + start_date
 * @param {string|Array} nameOrNames - a period name, a comma-joined list of
 *        them (the `period_names` column), or an array of period objects
 * @returns {string} YYYY-MM-DD of the earliest linked period, or '' when
 *          nothing resolves — unknowns sort together at one end rather than
 *          scattering.
 */
export const periodSortKey = (periods, nameOrNames) => {
  if (!nameOrNames) return '';

  // Already period objects (the Straights page hands us these) — just take the
  // earliest start_date and skip the name lookup entirely.
  if (Array.isArray(nameOrNames)) {
    const dates = nameOrNames.map(p => toISODate(p?.start_date)).filter(Boolean);
    return dates.length ? dates.sort()[0] : '';
  }

  if (!Array.isArray(periods) || !periods.length) return '';
  const names = String(nameOrNames).split(',').map(s => s.trim()).filter(Boolean);
  const dates = names
    .map(n => toISODate(periods.find(p => p.name === n)?.start_date))
    .filter(Boolean);
  return dates.length ? dates.sort()[0] : '';
};

/**
 * Find a period in the periods array that matches the current period
 * Checks by date range if exact name match not found
 *
 * @param {Array} periods - Array of period objects with id, name, start_date, end_date properties
 * @returns {string|null} Period ID as string, or null if not found
 */
export const getCurrentPeriodId = (periods) => {
  if (!periods || periods.length === 0) return null;

  const currentPeriodName = getCurrentPeriodName();

  // First try exact name match
  let currentPeriod = periods.find(p => p.name === currentPeriodName);

  // If no exact match, find period that contains today's date
  if (!currentPeriod) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    currentPeriod = periods.find(p => {
      const startDate = new Date(p.start_date);
      const endDate = new Date(p.end_date);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      return today >= startDate && today <= endDate;
    });
  }

  // If still no match, return the most recent period
  if (!currentPeriod && periods.length > 0) {
    const sortedPeriods = [...periods].sort((a, b) =>
      new Date(b.start_date) - new Date(a.start_date)
    );
    currentPeriod = sortedPeriods[0];
  }

  return currentPeriod ? currentPeriod.id.toString() : null;
};
